import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API, authHeaders, supabase } from "../lib/supabase";

const CACHE = "dropit-notifications-cache";
const POLL_MS = 30000;

/* Notifications list + unread count. Live via Supabase Realtime (INSERT on notifications, RLS-filtered);
   falls back to 30 s polling if the channel doesn't subscribe within 5 s; refetches on window focus. */
export const useNotifications = (userId, { onNew } = {}) => {
  const [state, setState] = useState(() => { try { return JSON.parse(localStorage.getItem(CACHE)) || { items: [], unread_count: 0 }; } catch { return { items: [], unread_count: 0 }; } });
  const [mode, setMode] = useState("idle");
  const seen = useRef(new Set());
  const primed = useRef(false);
  const onNewRef = useRef(onNew); onNewRef.current = onNew;

  const refetch = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const r = await fetch(`${API}/notifications?limit=50`, { headers: await authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      const fresh = d.items.filter((n) => !n.read_at && !seen.current.has(n.id));
      d.items.forEach((n) => seen.current.add(n.id));
      if (primed.current) fresh.forEach((n) => onNewRef.current?.(n));
      primed.current = true;
      setState(d);
      localStorage.setItem(CACHE, JSON.stringify(d));
    } catch { /* offline: keep cache */ }
  }, []);

  useEffect(() => {
    if (!userId) return;
    refetch();
    let poll = null; let subscribed = false;
    const startPoll = () => { if (!poll) { setMode("polling"); poll = setInterval(refetch, POLL_MS); } };
    const ch = supabase.channel(`notifications:${userId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, (payload) => {
        const n = payload.new;
        if (seen.current.has(n.id)) return;
        seen.current.add(n.id);
        setState((prev) => { const d = { items: [n, ...prev.items].slice(0, 50), unread_count: prev.unread_count + 1 }; localStorage.setItem(CACHE, JSON.stringify(d)); return d; });
        onNewRef.current?.(n);
      })
      .subscribe((status) => { if (status === "SUBSCRIBED") { subscribed = true; setMode("realtime"); if (poll) { clearInterval(poll); poll = null; } } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") startPoll(); });
    const fallback = setTimeout(() => { if (!subscribed) startPoll(); }, 5000);
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus); window.addEventListener("online", onFocus);
    return () => { clearTimeout(fallback); if (poll) clearInterval(poll); supabase.removeChannel(ch); window.removeEventListener("focus", onFocus); window.removeEventListener("online", onFocus); };
  }, [userId, refetch]);

  const markRead = useCallback(async (ids) => {
    const all = ids === "all";
    setState((prev) => { const now = new Date().toISOString(); const items = prev.items.map((n) => (all || ids.includes(n.id)) && !n.read_at ? { ...n, read_at: now } : n); const d = { items, unread_count: items.filter((n) => !n.read_at).length }; localStorage.setItem(CACHE, JSON.stringify(d)); return d; });
    try { await fetch(`${API}/notifications/read`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify(all ? { all: true } : { ids }) }); }
    catch { toast.error("Couldn't sync read state — will retry when online"); }
  }, []);

  return { ...state, mode, refetch, markRead };
};
